import { ArrowDown, ArrowUp, ArrowUpDown, CheckCircle2, ChevronLeft, ChevronRight, Circle, Download, ExternalLink, PencilLine, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Avatar } from '../components/Avatar'
import { Button } from '../components/Button'
import { ContentSummary, getClientColorSafe } from '../components/ContentSummary'
import { CloseMonth } from '../components/CloseMonth'
import { MonthRecap } from '../components/MonthRecap'
import { MonthHealth } from '../components/MonthHealth'
import { MonthStepper, RequirementsPanel, isContentRequirement } from '../components/Requirements'
import { Dropdown } from '../components/Dropdown'
import { EditableDate, EditableSelect, EditableText } from '../components/EditableCell'
import { FilterBar, FilterSelect, SearchInput } from '../components/FilterBar'
import { PageHeader } from '../components/PageHeader'
import { useAppData } from '../context/AppDataContext'
import { useToast } from '../context/ToastContext'
import { useScopedWork } from '../hooks/useScopedWork'
import {
  TASK_STATUSES,
  DONE_STATUS,
  PLATFORMS,
  CONTENT_TYPES,
  type ApprovalStatus,
  type ContentItem,
  type ContentPlatform,
  type ContentType,
  type PostingStatus,
  type SimpleStatus,
  type TaskStatus,
} from '../data/types'
import { cn } from '../lib/cn'
import { downloadCsv } from '../lib/csv'
import { useAuth } from '../context/AuthContext'
import { monthName, shortDate, today } from '../lib/format'
import { NewContentModal } from './content/NewContentModal'
import { Select } from '../components/Select'

type SortColumn = 'date' | 'client' | 'platform' | 'type' | 'title' | 'status' | 'assigned'
type SortDirection = 'asc' | 'desc'

const statusOptions: TaskStatus[] = TASK_STATUSES
const simpleOptions: SimpleStatus[] = ['Not Started', 'In Progress', 'Done']
const approvalOptions: ApprovalStatus[] = ['Pending', 'Approved', 'Changes Requested']
const postingOptions: PostingStatus[] = ['Not Scheduled', 'Scheduled', 'Posted']

const statusColors: Record<string, { bg: string; text: string }> = {
  Backlog: { bg: '#ececee', text: '#4a4a56' },
  'Editing': { bg: '#eaf1fd', text: '#2c63b8' },
  'Shot': { bg: '#fef6e7', text: '#b3730f' },
  Review: { bg: '#ffe0e9', text: '#e01050' },
  Completed: { bg: '#edf7ee', text: '#257f46' },
  'Not Started': { bg: '#ececee', text: '#4a4a56' },
  Done: { bg: '#edf7ee', text: '#257f46' },
  Pending: { bg: '#fef6e7', text: '#b3730f' },
  Approved: { bg: '#edf7ee', text: '#257f46' },
  'Changes Requested': { bg: '#fdeeee', text: '#c22f27' },
  'Not Scheduled': { bg: '#ececee', text: '#4a4a56' },
  Scheduled: { bg: '#eaf1fd', text: '#2c63b8' },
  Posted: { bg: '#edf7ee', text: '#257f46' },
}

export default function ContentCalendar() {
  const { updateContent, directory, month, setMonth, requirements } = useAppData()
  const { content, clients } = useScopedWork()
  // Prospects owe nothing and have nothing going out: the plan is for clients.
  const activeClients = clients.filter(c => c.accountStatus !== 'Former' && c.accountStatus !== 'Lost' && c.accountStatus !== 'Prospect')
  const { showToast } = useToast()
  const [clientTab, setClientTab] = useState('all')
  const [search, setSearch] = useState('')
  const [platformFilter, setPlatformFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [newContentOpen, setNewContentOpen] = useState(false)
  const tabsRef = useRef<HTMLDivElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)

  // The table is much wider than the viewport (fixed min-width so every
  // column stays readable), but a mouse has no easy way to scroll it
  // horizontally the way a trackpad swipe can — these buttons jump a
  // fixed distance either direction instead.
  function scrollTable(direction: 1 | -1) {
    tableScrollRef.current?.scrollBy({ left: direction * 320, behavior: 'smooth' })
  }

  // The client-tab strip only scrolls horizontally, but a mouse (unlike a
  // trackpad) has no horizontal wheel axis — so redirect a normal vertical
  // scroll gesture into horizontal movement here instead of scrolling the
  // page. This has to be a native, non-passive listener: React attaches
  // onWheel as passive, which silently ignores preventDefault() and lets
  // the page scroll anyway underneath our redirect.
  useEffect(() => {
    const el = tabsRef.current
    if (!el) return
    const handler = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
      e.preventDefault()
      el.scrollLeft += e.deltaY
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [])

  // What a piece can count towards: its client's requirements in the month
  // that owes it. Only known for this month's pieces; one owed by another
  // month says which line it counts on there, and is changed from that month.
  const linesFor = useCallback(
    (clientId: string) => requirements.filter((r) => r.client_id === clientId && r.commitment_id !== null && isContentRequirement(r)),
    [requirements]
  )

  const clientName = useCallback((id: string) => clients.find((cl) => cl.id === id)?.name ?? '', [clients])
  const assignee = (id: string) => directory.find((m) => m.id === id)
  const assigneeName = (id: string) => assignee(id)?.name ?? ''

  const filtered = useMemo(() => {
    const list = content.filter((c) => {
      if (clientTab !== 'all' && c.clientId !== clientTab) return false
      if (platformFilter !== 'all' && c.platform !== platformFilter) return false
      if (statusFilter !== 'all' && c.status !== statusFilter) return false
      if (search && !c.title.toLowerCase().includes(search.toLowerCase()) && !c.topic.toLowerCase().includes(search.toLowerCase())) return false
      return true
    })
    if (!sortColumn) return list
    const dir = sortDirection === 'asc' ? 1 : -1
    const key = (c: ContentItem) => {
      switch (sortColumn) {
        case 'date':
          return c.date
        case 'client':
          return clientName(c.clientId)
        case 'platform':
          return c.platform
        case 'type':
          return c.contentType
        case 'title':
          return c.title
        case 'status':
          return c.status
        case 'assigned':
          return assigneeName(c.assignedToId)
      }
    }
    return [...list].sort((a, b) => key(a).localeCompare(key(b)) * dir)
  }, [content, clientTab, platformFilter, statusFilter, search, sortColumn, sortDirection, clientName])

  function toggleSort(col: SortColumn) {
    if (sortColumn !== col) {
      setSortColumn(col)
      setSortDirection('asc')
    } else {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    }
  }

  function patch(id: string, fields: Partial<ContentItem>) {
    if (fields.status === DONE_STATUS) {
      // Design, editing and posting are the studio's own states, and ticking
      // the row does assert all three. Approval is not: it is the client's
      // word, and writing it here on their behalf both invents a sign-off
      // nobody gave and walks the piece straight past the gate that exists to
      // record exactly that. Left out, an unapproved piece asks first.
      fields = {
        ...fields,
        designStatus: 'Done',
        editingStatus: 'Done',
        postingStatus: 'Posted',
        postedDate: today(),
      }
    }
    updateContent(id, fields)
  }

  function toggleComplete(item: ContentItem) {
    const isDone = item.status === DONE_STATUS
    const was = item.status
    patch(item.id, { status: isDone ? 'Editing' : DONE_STATUS })
    showToast(isDone ? 'Marked as not done.' : 'Content marked complete.', 'success', {
      undo: () => patch(item.id, { status: was }),
    })
  }

  /*
   * Creating a piece is a modal now, not a row.
   *
   * This used to drop a row called "Untitled content" into the sheet, dated a
   * fixed day in September, and leave somebody to edit it across a dozen
   * inline cells — which meant the plan always held a few half-made rows
   * nobody could tell from real ones. The modal asks for the five things that
   * make a piece findable and lets the sheet keep its job, which is
   * everything after that.
   */

  // An Admin can switch exporting off per role in the permissions matrix.
  const canExport = useAuth().hasPermission('content', 'export')

  function exportCsv() {
    downloadCsv(
      'content_calendar_export.csv',
      filtered.map((c) => ({
        Date: c.date,
        Client: clients.find((cl) => cl.id === c.clientId)?.name ?? '',
        Platform: c.platform,
        ContentType: c.contentType,
        Title: c.title,
        Topic: c.topic,
        Status: c.status,
        AssignedTo: directory.find((m) => m.id === c.assignedToId)?.name ?? '',
        DesignStatus: c.designStatus,
        EditingStatus: c.editingStatus,
        ApprovalStatus: c.approvalStatus,
        PostingStatus: c.postingStatus,
        PostedDate: c.postedDate ?? '',
        ReelLink: c.reelLink,
        Notes: c.notes,
      })),
    )
    showToast('Content calendar exported as CSV.')
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        variant="dark"
        title="Content plan"
        description="What is going out, when, and what it says. This is the reels sheet."
        actions={
          <>
            <MonthStepper month={month} onChange={setMonth} />
            {canExport && (
              <Button variant="secondary" onClick={exportCsv}>
                <Download size={15} /> Export CSV
              </Button>
            )}
            {/* Write mode: the same pieces as a spreadsheet, full screen in a
                tab of its own, one sheet per client. */}
            <Button
              variant="secondary"
              onClick={() =>
                window.open(
                  clientTab === 'all' ? '/content/sheet' : `/content/sheet?client=${encodeURIComponent(clientTab)}`,
                  '_blank',
                  'noopener'
                )
              }
            >
              <PencilLine size={15} /> Write mode
            </Button>
            <Button onClick={() => setNewContentOpen(true)}>
              <Plus size={16} /> Add a piece
            </Button>
          </>
        }
      />

      <div ref={tabsRef} className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
        <button
          onClick={() => setClientTab('all')}
          className={cn(
            'shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
            clientTab === 'all' ? 'bg-shell-text-active text-shell' : 'bg-shell text-shell-text border border-shell-border hover:bg-shell-border/40',
          )}
        >
          All Clients
        </button>
        {activeClients.map((c) => (
          <button
            key={c.id}
            onClick={() => setClientTab(c.id)}
            className={cn(
              'shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              clientTab === c.id ? 'text-white' : 'bg-shell text-shell-text border border-shell-border hover:bg-shell-border/40',
            )}
            style={clientTab === c.id ? { backgroundColor: c.color } : undefined}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* The month's own pieces, so a reel owed by last month but posting
          in this one is not counted twice. */}
      <ContentSummary
        content={content.filter((c) => c.cycleMonth === undefined || c.cycleMonth === month)}
        clientId={clientTab}
        color={getClientColorSafe(clientTab, clients)}
        requirements={requirements}
      />

      <MonthHealth content={content} month={month} clientId={clientTab} />

      {/* Settling a month is about one client's retainer, so it is offered
          when one client is in view and not over "all clients" — closing
          everybody's month at once is not a button anybody should have. */}
      {clientTab !== 'all' && (
        <div className="flex flex-col gap-3">
          <MonthRecap clientId={clientTab} month={month} />
          <div className="flex justify-end">
          <CloseMonth
            clientId={clientTab}
            clientName={clientName(clientTab) || 'this client'}
            month={month}
            closedAt={requirements.find((r) => r.client_id === clientTab)?.closed_at ?? null}
            unfinished={
              content.filter(
                (c) => c.clientId === clientTab && (c.cycleMonth ?? month) === month && c.status !== DONE_STATUS
              ).length
            }
          />
          </div>
        </div>
      )}

      <RequirementsPanel requirements={requirements} clientId={clientTab} month={month} />

      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search titles and topics…" className="w-full sm:w-64" />
        <FilterSelect
          label="Filter by platform"
          value={platformFilter}
          onChange={setPlatformFilter}
          options={[{ label: 'All PLATFORMS', value: 'all' }, ...PLATFORMS.map((p) => ({ label: p, value: p }))]}
        />
        <FilterSelect
          label="Filter by status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[{ label: 'All statuses', value: 'all' }, ...statusOptions.map((s) => ({ label: s, value: s }))]}
        />
        <span className="text-xs text-shell-text">{filtered.length} rows</span>
      </FilterBar>

      <div className="relative">
        {/* On phones these sit in their own right-aligned row above the
          * table — floated, they landed on top of the row count and the
          * table's top edge with no clearance. From `sm` up there's room to
          * hang them in the gap as before. */}
        <div className="mb-2 flex items-center justify-end gap-1.5 sm:pointer-events-none sm:absolute sm:-top-8 sm:right-3 sm:z-20 sm:mb-0">
          <button
            onClick={() => scrollTable(-1)}
            aria-label="Scroll table left"
            className="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-full bg-brand-500 text-white shadow-pop transition-transform hover:scale-105 hover:bg-brand-600 active:scale-95"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => scrollTable(1)}
            aria-label="Scroll table right"
            className="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-full bg-brand-500 text-white shadow-pop transition-transform hover:scale-105 hover:bg-brand-600 active:scale-95"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <div ref={tableScrollRef} className="overflow-x-auto rounded-card border border-shell-border bg-shell shadow-card scrollbar-thin">
        <table className="w-full text-left text-sm" style={{ minWidth: 1900 }}>
          <thead>
            <tr className="border-b border-shell-border bg-shell-border/30 text-[11px] font-semibold uppercase tracking-wide text-shell-text">
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5" title="Mark it posted">
                Done
              </th>
              <SortHeader label="Date" column="date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />
              <SortHeader label="Client" column="client" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />
              <SortHeader label="Platform" column="platform" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />
              <SortHeader label="Type" column="type" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5" title="The requirement this piece counts towards">
                Counts towards
              </th>
              <SortHeader label="Title" column="title" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5">Topic</th>
              <SortHeader label="Status" column="status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />
              <SortHeader label="Assigned" column="assigned" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5">Design</th>
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5">Editing</th>
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5">Approval</th>
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5">Posting</th>
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5">Posted Date</th>
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5">Reel Link</th>
              <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5">Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const currentAssignee = assignee(c.assignedToId)
              return (
              <tr key={c.id} className="border-b border-shell-border/60 last:border-0 hover:bg-shell-border/20">
                <td className="px-3 py-1 text-center">
                  <button
                    onClick={() => toggleComplete(c)}
                    aria-label={c.status === DONE_STATUS ? 'Mark as not done' : 'Mark complete'}
                    className="text-shell-text/50 transition-colors hover:text-success-500"
                  >
                    {c.status === DONE_STATUS ? <CheckCircle2 size={17} className="text-success-500" /> : <Circle size={17} />}
                  </button>
                </td>
                <td className="px-1 py-1">
                  <EditableDate value={c.date} onSave={(v) => patch(c.id, { date: v })} />
                  {c.cycleMonth !== undefined && c.cycleMonth !== month && (
                    <span className="block px-2 text-[10px] text-shell-text/70" title="It counts in the month that owes it">
                      Owed in {monthName(c.cycleMonth)}
                    </span>
                  )}
                  {c.cycleMonth === month && c.date !== '' && c.date.slice(0, 7) !== month.slice(0, 7) && (
                    <span
                      className="block px-2 text-[10px] text-amber-700"
                      title={
                        c.monthPinned === true
                          ? 'Owed this month and went out in another — kept here on purpose, so it still counts where it was promised'
                          : 'Owed this month, going out in another'
                      }
                    >
                      {c.monthPinned === true ? 'Late — out ' : 'Goes out '}
                      {shortDate(c.date)}
                    </span>
                  )}
                </td>
                <td className="px-1 py-1">
                  <Select
                    value={c.clientId}
                    onChange={(e) => patch(c.id, { clientId: e.target.value })}
                    className="w-36 rounded-md border border-transparent bg-transparent px-2 py-1.5 text-sm text-shell-text outline-none transition-colors hover:bg-shell-border/40 focus:border-brand-400 focus:bg-shell"
                  >
                    {clients.map((cl) => (
                      <option key={cl.id} value={cl.id}>
                        {cl.name}
                      </option>
                    ))}
                  </Select>
                </td>
                <td className="px-1 py-1">
                  <EditableSelect value={c.platform} onSave={(v: ContentPlatform) => patch(c.id, { platform: v })} options={PLATFORMS} width="w-28" />
                </td>
                <td className="px-1 py-1">
                  <EditableSelect value={c.contentType} onSave={(v: ContentType) => patch(c.id, { contentType: v })} options={CONTENT_TYPES} width="w-28" />
                </td>
                <td className="px-1 py-1">
                  {c.cycleMonth === undefined || c.cycleMonth === month ? (
                    <Select
                      value={c.commitmentId ?? ''}
                      onChange={(e) => {
                        patch(c.id, { commitmentId: e.target.value })
                        showToast(e.target.value === '' ? 'No longer counted.' : 'Counted towards it.')
                      }}
                      className={cn(
                        'w-40 rounded-md border border-transparent bg-transparent px-2 py-1.5 text-sm outline-none transition-colors hover:bg-shell-border/40 focus:border-brand-400 focus:bg-shell',
                        c.commitmentId ? 'text-shell-text' : 'text-amber-700'
                      )}
                    >
                      <option value="">Not counted</option>
                      {linesFor(c.clientId).map((r) => (
                        <option key={r.commitment_id!} value={r.commitment_id!}>
                          {r.metric}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <span className="block w-40 truncate px-2 text-sm text-shell-text" title={`Changed from ${monthName(c.cycleMonth)}`}>
                      {c.commitment ?? 'Not counted'}
                    </span>
                  )}
                </td>
                <td className="px-1 py-1">
                  <EditableText
                    value={c.title}
                    onSave={(v) => patch(c.id, { title: v })}
                    width="w-52"
                    className={cn(c.status === DONE_STATUS && 'text-shell-text/50 line-through')}
                  />
                </td>
                <td className="px-1 py-1">
                  <EditableText value={c.topic} onSave={(v) => patch(c.id, { topic: v })} width="w-40" />
                </td>
                <td className="px-1 py-1">
                  <EditableSelect value={c.status} onSave={(v: TaskStatus) => patch(c.id, { status: v })} options={statusOptions} colors={statusColors} width="w-28" />
                </td>
                <td className="px-1 py-1">
                  {/*
                    * A face rather than a name. Reassigning is the most
                    * frequent edit on this sheet and the studio is four
                    * people, so the colour is recognised before the word is
                    * read — and the unassigned case now says so instead of
                    * showing whoever happens to be first in the roster.
                    */}
                  <Dropdown
                    trigger={
                      <span className="flex w-32 items-center gap-1.5 truncate rounded-md px-2 py-1.5 text-sm text-shell-text transition-colors hover:bg-shell-border/40">
                        {currentAssignee !== undefined && (
                          <Avatar name={currentAssignee.name} color={currentAssignee.color} size="xs" />
                        )}
                        <span className="truncate">{currentAssignee?.name ?? 'Nobody yet'}</span>
                      </span>
                    }
                    items={directory.map((m) => ({
                      label: m.name,
                      value: m.id,
                      icon: <Avatar name={m.name} color={m.color} size="xs" />,
                    }))}
                    onSelect={(v) => patch(c.id, { assignedToId: v })}
                  />
                </td>
                <td className="px-1 py-1">
                  <EditableSelect value={c.designStatus} onSave={(v: SimpleStatus) => patch(c.id, { designStatus: v })} options={simpleOptions} colors={statusColors} width="w-28" />
                </td>
                <td className="px-1 py-1">
                  <EditableSelect value={c.editingStatus} onSave={(v: SimpleStatus) => patch(c.id, { editingStatus: v })} options={simpleOptions} colors={statusColors} width="w-28" />
                </td>
                <td className="px-1 py-1">
                  <EditableSelect value={c.approvalStatus} onSave={(v: ApprovalStatus) => patch(c.id, { approvalStatus: v })} options={approvalOptions} colors={statusColors} width="w-36" />
                </td>
                <td className="px-1 py-1">
                  <EditableSelect
                    value={c.postingStatus}
                    onSave={(v: PostingStatus) => patch(c.id, { postingStatus: v, postedDate: v === 'Posted' ? today() : c.postedDate })}
                    options={postingOptions}
                    colors={statusColors}
                    width="w-32"
                  />
                </td>
                <td className="px-1 py-1">
                  <EditableDate value={c.postedDate ?? ''} onSave={(v) => patch(c.id, { postedDate: v || null })} />
                </td>
                <td className="px-1 py-1">
                  {c.reelLink ? (
                    <a href={c.reelLink} target="_blank" rel="noreferrer" className="flex w-28 items-center gap-1 truncate rounded-md px-2 py-1 text-sm text-brand-500 hover:bg-shell-border/40">
                      <ExternalLink size={12} /> Link
                    </a>
                  ) : (
                    <EditableText value={c.reelLink} onSave={(v) => patch(c.id, { reelLink: v })} width="w-28" placeholder="Add link" />
                  )}
                </td>
                <td className="px-1 py-1">
                  <EditableText value={c.notes} onSave={(v) => patch(c.id, { notes: v })} width="w-48" placeholder="Add note" />
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="py-10 text-center text-sm text-shell-text">No content matches your filters.</p>}
        </div>
      </div>

      <NewContentModal
        open={newContentOpen}
        onClose={() => setNewContentOpen(false)}
        defaultClientId={clientTab === 'all' ? undefined : clientTab}
      />
    </div>
  )
}

function SortHeader({
  label,
  column,
  sortColumn,
  sortDirection,
  onSort,
}: {
  label: string
  column: SortColumn
  sortColumn: SortColumn | null
  sortDirection: SortDirection
  onSort: (col: SortColumn) => void
}) {
  const active = sortColumn === column
  const Icon = !active ? ArrowUpDown : sortDirection === 'asc' ? ArrowUp : ArrowDown
  return (
    <th className="sticky top-0 whitespace-nowrap bg-shell-border/30 px-3 py-2.5">
      <button
        onClick={() => onSort(column)}
        className={cn('flex items-center gap-1 transition-colors hover:text-shell-text-active', active && 'text-brand-500')}
      >
        {label}
        <Icon size={11} className={cn(!active && 'text-shell-text/50')} />
      </button>
    </th>
  )
}
